import { formatUsZipForDirections } from './formatUsZipForDirections';

type DirectionsMapsApi = {
  DirectionsService: new () => {
    route: (
      request: google.maps.DirectionsRequest,
      callback?: (
        result: google.maps.DirectionsResult | null,
        status: google.maps.DirectionsStatus
      ) => void
    ) => Promise<google.maps.DirectionsResult> | void;
  };
  TravelMode: { DRIVING: google.maps.TravelMode };
};

function isThenable(
  value: unknown
): value is Promise<google.maps.DirectionsResult> {
  return (
    value != null &&
    typeof (value as { then?: unknown }).then === 'function'
  );
}

/**
 * Safe DirectionsService.route wrapper.
 *
 * When Maps auth fails (e.g. RefererNotAllowedMapError), route() may return
 * undefined instead of a Promise — chaining .catch then crashes the page.
 */
export async function requestDrivingDirections(
  origin: string,
  destination: string,
  maps: DirectionsMapsApi | undefined = (
    typeof window !== 'undefined'
      ? (window.google?.maps as DirectionsMapsApi | undefined)
      : undefined
  )
): Promise<google.maps.DirectionsResult | null> {
  if (!origin || !destination) {
    return null;
  }

  if (!maps?.DirectionsService || !maps.TravelMode?.DRIVING) {
    console.warn(
      'Directions unavailable: Google Maps DirectionsService not loaded'
    );
    return null;
  }

  const request: google.maps.DirectionsRequest = {
    origin: formatUsZipForDirections(origin),
    destination: formatUsZipForDirections(destination),
    travelMode: maps.TravelMode.DRIVING,
    region: 'us',
  };

  try {
    const directionsService = new maps.DirectionsService();

    let maybePromise: Promise<google.maps.DirectionsResult> | void;
    try {
      maybePromise = directionsService.route(request);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : String(err ?? 'unknown error');
      console.warn('Directions request failed:', message);
      return null;
    }

    if (!isThenable(maybePromise)) {
      console.warn(
        'Directions request failed: route() returned no Promise (Maps may be unauthorized)'
      );
      return null;
    }

    try {
      return await maybePromise;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : String(err ?? 'unknown error');
      console.warn('Directions request failed:', message);
      return null;
    }
  } catch (err) {
    const message =
      err instanceof Error ? err.message : String(err ?? 'unknown error');
    console.warn('Directions request failed:', message);
    return null;
  }
}
